import apiClient, { buildAuthHeaders } from 'services/apiClient';

const VEHICLE_COMPLAINT_SCHEMA_ENDPOINT = 'shiftware/api/v1/vehicle-complaints/schema/';
const DEFAULT_LIMIT = 20;

const authJsonHeaders = (token, extraHeaders = {}) => ({
    'Content-Type': 'application/json',
    ...buildAuthHeaders(token),
    ...extraHeaders,
});

const vehicleComplaintsEndpoint = (chassisNumber) => (
    `shiftware/api/v1/vehicles/${encodeURIComponent(chassisNumber)}/complaints/`
);

const normalizeChoice = (choice, fallbackValue = '') => {
    if (choice && typeof choice === 'object') {
        const value = choice.value ?? fallbackValue;
        return {
            value,
            label: choice.label ?? value,
        };
    }

    const value = choice || fallbackValue;
    return {
        value,
        label: value,
    };
};

export const normalizeVehicleComplaint = (complaint = {}) => {
    const history = complaint.history || {};
    const timelineAt = history.timeline_at
        || complaint.customer_submission_date
        || complaint.staff_reported_date
        || null;
    const id = complaint.id ?? complaint.claim_id;

    return {
        ...complaint,
        id,
        claim_id: complaint.claim_id ?? id,
        description: complaint.description ?? complaint.customer_complaint_description ?? '',
        category: normalizeChoice(complaint.category, complaint.customer_ticket_category),
        status: normalizeChoice(complaint.status, complaint.action || complaint.issue_resolution_status || 'open'),
        history: {
            timeline_at: timelineAt,
            vehicle_name_at_submission: history.vehicle_name_at_submission ?? null,
            product_version_at_submission: history.product_version_at_submission ?? null,
            submitted_at: history.submitted_at ?? complaint.customer_submission_date ?? null,
            created_at: history.created_at ?? complaint.staff_reported_date ?? null,
            first_response_at: history.first_response_at ?? complaint.first_response_time ?? null,
            resolved_at: history.resolved_at ?? complaint.complaint_closing_date ?? complaint.issue_resolution_time ?? null,
        },
    };
};

export const normalizeVehicleComplaintPage = (payload = {}) => {
    const rawResults = Array.isArray(payload) ? payload : payload.results || [];

    return {
        results: rawResults.map(normalizeVehicleComplaint),
        hasMore: Boolean(payload.has_more),
        nextCursor: payload.next_cursor || null,
        limit: payload.limit || DEFAULT_LIMIT,
    };
};

export const getVehicleComplaintSchema = async ({ token } = {}) => {
    const response = await apiClient.get(VEHICLE_COMPLAINT_SCHEMA_ENDPOINT, {
        headers: authJsonHeaders(token),
    });
    return response.data;
};

export const listVehicleComplaints = async ({
    chassisNumber,
    token,
    status,
    category,
    limit = DEFAULT_LIMIT,
    cursor,
} = {}) => {
    const params = {
        limit,
        ...(status ? { status } : {}),
        ...(category ? { category } : {}),
        ...(cursor ? { cursor } : {}),
    };

    const response = await apiClient.get(vehicleComplaintsEndpoint(chassisNumber), {
        headers: authJsonHeaders(token),
        params,
    });
    return normalizeVehicleComplaintPage(response.data);
};

export const createVehicleComplaint = async ({
    chassisNumber,
    token,
    payload,
    idempotencyKey,
} = {}) => {
    const response = await apiClient.post(
        vehicleComplaintsEndpoint(chassisNumber),
        payload,
        {
            headers: authJsonHeaders(token, idempotencyKey ? {
                'Idempotency-Key': idempotencyKey,
            } : {}),
        }
    );
    return normalizeVehicleComplaint(response.data);
};
